import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Typography, Button, Box, Container, Snackbar, Alert } from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format } from "date-fns";
import { authAxios } from "../../contexts/AuthContext";
import { API_BASE_URL } from "../../api/http";
import PlayerCard from "../../components/PlayerCard/PlayerCard";
import PageLoader from "../../components/PageLoader/PageLoader";
import ReviewDiscussionSection from "../../components/ReviewDiscussion/ReviewDiscussionSection";
import {
  ALL_TRAIT_KEYS,
  isGoalie,
  traitFieldsForPlayer,
} from "../CreateRanking/rankingTraitFields";
import { userDisplayName } from "../../utils/userDisplayName";
import { isRankingPublished, isCompetitionEntry } from "../../utils/independentRanking";
import Top64CompetitionScore from "../../components/Top64CompetitionScore/Top64CompetitionScore";
import "./IndependentRanking.css";

function rowHasNote(row) {
  return Boolean(row.note?.trim());
}

function rowHasAttributes(row) {
  return ALL_TRAIT_KEYS.some((key) => row[key] != null && row[key] !== "");
}

function playerDisplayName(player) {
  if (!player) return "";
  return [player.first_name, player.last_name].filter(Boolean).join(" ").trim();
}

function truncateMetaText(text, maxLength) {
  if (!text) return "";
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 3).trim()}...`;
}

function IndependentRanking() {
  const [ranking, setRanking] = useState(null);
  const { independent_ranking_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [shareSnackbarOpen, setShareSnackbarOpen] = useState(false);
  const [shareSnackbarMessage, setShareSnackbarMessage] = useState("");
  const [showFloatingShare, setShowFloatingShare] = useState(false);

  useEffect(() => {
    setLoading(true);
    authAxios
      .get(`/independent_rankings/${independent_ranking_id}`)
      .then((response) => {
        setRanking(response.data);
        setLoading(false);
      })
      .catch(() => {
        setRanking(null);
        setLoading(false);
      });
  }, [independent_ranking_id]);

  const sortedRankings = useMemo(
    () =>
      ranking?.rankings
        ? [...ranking.rankings].sort((a, b) => (a.rank ?? 999) - (b.rank ?? 999))
        : [],
    [ranking]
  );

  const includeNotes = sortedRankings.some(rowHasNote);
  const includeAttributes = sortedRankings.some(rowHasAttributes);
  const cardsOnlyLayout = !includeNotes && !includeAttributes;

  const competitionPickByPlayerId = useMemo(() => {
    const breakdown = ranking?.competition_score?.breakdown;
    if (!breakdown) return {};
    return Object.fromEntries(
      breakdown
        .filter((row) => row.predicted_rank != null)
        .map((row) => [row.player_id, row])
    );
  }, [ranking]);

  const authorName = userDisplayName(ranking?.user);
  const authorUserId = ranking?.user_id || ranking?.user?.id;

  const canonicalRankingUrl = `https://hockeypipeline.com/independent-rankings/${independent_ranking_id}`;
  const shareOrigin =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
      ? API_BASE_URL
      : window.location.origin;
  const shareUrl = `${shareOrigin}/share/independent-rankings/${independent_ranking_id}?v=6`;
  const shareImageUrl = "https://hockeypipeline.com/share-card.png";

  const previewTitle = ranking
    ? truncateMetaText(`${ranking.title} | ${ranking.draft_year} NHL Draft Ranking`, 95)
    : "Independent Ranking | Hockey Pipeline";

  const previewDescription = useMemo(() => {
    if (!ranking) return "View this NHL Draft prospect ranking on Hockey Pipeline.";
    if (ranking.description?.trim()) {
      return truncateMetaText(ranking.description, 200);
    }
    const topPlayers = sortedRankings
      .slice(0, 5)
      .map((row) => playerDisplayName(row.player))
      .filter(Boolean)
      .join(", ");
    if (topPlayers) {
      return truncateMetaText(
        `${authorName}'s ${ranking.draft_year} NHL Draft board starts with ${topPlayers}. View the full ${sortedRankings.length}-player ranking on Hockey Pipeline.`,
        200
      );
    }
    return truncateMetaText(
      `${authorName}'s ${ranking.draft_year} NHL Draft prospect ranking on Hockey Pipeline.`,
      200
    );
  }, [ranking, sortedRankings, authorName]);

  const canShare = ranking ? isRankingPublished(ranking) : false;

  useEffect(() => {
    if (!canShare || loading) {
      setShowFloatingShare(false);
      return undefined;
    }

    const bottomThreshold = 100;

    const updateFloatingShare = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const atBottom = scrollTop + clientHeight >= scrollHeight - bottomThreshold;
      setShowFloatingShare(atBottom);
    };

    updateFloatingShare();
    window.addEventListener("scroll", updateFloatingShare, { passive: true });
    window.addEventListener("resize", updateFloatingShare, { passive: true });

    return () => {
      window.removeEventListener("scroll", updateFloatingShare);
      window.removeEventListener("resize", updateFloatingShare);
    };
  }, [canShare, loading, sortedRankings.length]);

  const handleShare = useCallback(async () => {
    if (!ranking || !isRankingPublished(ranking)) return;

    const sharePayload = {
      title: previewTitle,
      text: previewDescription,
      url: shareUrl,
    };

    if (navigator.share) {
      try {
        await navigator.share(sharePayload);
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareSnackbarMessage("Link copied to clipboard!");
    } catch {
      setShareSnackbarMessage("Could not copy link. Please copy the URL from your browser.");
    }
    setShareSnackbarOpen(true);
  }, [ranking, previewTitle, previewDescription, shareUrl]);

  const handleShareSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") return;
    setShareSnackbarOpen(false);
  };

  return (
    <>
      {ranking && (
        <Helmet>
          <title>
            {ranking.draft ? "Preview: " : ""}
            {ranking.title} - Independent Rankings - Hockey Pipeline
          </title>
          <meta
            name="description"
            content={previewDescription}
          />
          {!ranking.draft && (
            <>
              <link rel="canonical" href={canonicalRankingUrl} />
              <meta property="og:type" content="article" />
              <meta property="og:site_name" content="Hockey Pipeline" />
              <meta property="og:title" content={previewTitle} />
              <meta property="og:description" content={previewDescription} />
              <meta property="og:url" content={shareUrl} />
              <meta property="og:image" content={shareImageUrl} />
              <meta property="og:image:secure_url" content={shareImageUrl} />
              <meta property="og:image:type" content="image/png" />
              <meta property="og:image:width" content="1200" />
              <meta property="og:image:height" content="630" />
              <meta property="og:image:alt" content="Hockey Pipeline logo" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content={previewTitle} />
              <meta name="twitter:description" content={previewDescription} />
              <meta name="twitter:image" content={shareImageUrl} />
              <meta name="twitter:image:alt" content="Hockey Pipeline logo" />
            </>
          )}
          {ranking.draft && <meta name="robots" content="noindex" />}
        </Helmet>
      )}
      <PageLoader loading={loading} />
      <div className="independent-ranking-shell top-container">
        <Container className="container independent-ranking-page">
          {ranking && (
            <>
              {ranking.draft && (
                <div className="independent-ranking-preview-banner" role="status">
                  <Box>
                    <Typography className="independent-ranking-preview-banner-text">
                      <span className="independent-ranking-preview-banner-label">
                        Preview
                      </span>
                      {isCompetitionEntry(ranking)
                        ? "This is your competition entry. It stays private until entries are released after the deadline."
                        : "This is how your ranking will appear after you publish. It is still a draft and is not visible to anyone else."}
                    </Typography>
                  </Box>
                  <div className="independent-ranking-preview-banner-actions">
                    <Button
                      component={Link}
                      to="/your-rankings"
                      variant="outlined"
                      size="small"
                    >
                      Your rankings
                    </Button>
                    <Button
                      component={Link}
                      to={`/your-rankings/${independent_ranking_id}/edit`}
                      variant="contained"
                      size="small"
                      disableElevation
                    >
                      Edit ranking
                    </Button>
                  </div>
                </div>
              )}

              <header
                className={[
                  "independent-ranking-header",
                  canShare ? "independent-ranking-header--has-share" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {canShare && (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    className="independent-ranking-share-btn"
                    startIcon={<ShareOutlinedIcon sx={{ fontSize: 15 }} />}
                    onClick={handleShare}
                  >
                    Share
                  </Button>
                )}
                <Typography variant="h4" component="h1" className="page-title">
                  {ranking.title}
                </Typography>
                <div className="title-underline" />
                {ranking.description && (
                  <p className="independent-ranking-description">{ranking.description}</p>
                )}
                <div className="independent-ranking-meta">
                  <div className="independent-ranking-meta-dates">
                    <div className="independent-ranking-meta-field">
                      <span className="independent-ranking-meta-label">Draft year</span>
                    <span className="independent-ranking-meta-value">
                      {ranking.draft_year}
                    </span>
                  </div>
                  {ranking.creation_date && (
                    <div className="independent-ranking-meta-field">
                      <span className="independent-ranking-meta-label">
                        {ranking.draft ? "Created" : "Published"}
                      </span>
                      <span className="independent-ranking-meta-value">
                        {format(new Date(ranking.creation_date), "MMM d, yyyy")}
                      </span>
                    </div>
                  )}
                  </div>
                  <div className="independent-ranking-meta-field">
                    <span className="independent-ranking-meta-label">Author</span>
                    {authorUserId ? (
                      <Link
                        to={`/profile/${authorUserId}`}
                        className="independent-ranking-meta-value link-default"
                      >
                        {authorName}
                      </Link>
                    ) : (
                      <span className="independent-ranking-meta-value">{authorName}</span>
                    )}
                  </div>
                  {ranking.official_ranking?.name && (
                    <div className="independent-ranking-meta-field">
                      <span className="independent-ranking-meta-label">
                        Official ranking
                      </span>
                      <span className="independent-ranking-meta-value">
                        {ranking.official_ranking.name}
                      </span>
                    </div>
                  )}
                  {ranking.url && (
                    <div className="independent-ranking-meta-field">
                      <span className="independent-ranking-meta-label">Source</span>
                      <a
                        href={ranking.url}
                        className="independent-ranking-meta-value independent-ranking-meta-link link-default"
                        target="_blank"
                        rel="noreferrer"
                      >
                        View source
                      </a>
                    </div>
                  )}
                </div>
              </header>

              {sortedRankings.length > 0 && (
                <p className="independent-ranking-count">
                  {sortedRankings.length} player
                  {sortedRankings.length === 1 ? "" : "s"} ranked
                </p>
              )}

              {isCompetitionEntry(ranking) && ranking.competition_score && (
                <Top64CompetitionScore competitionScore={ranking.competition_score} />
              )}

              <div
                className={[
                  "independent-ranking-list",
                  cardsOnlyLayout ? "independent-ranking-list--cards-only" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {sortedRankings.map((row) => {
                  const player = row.player;
                  const competitionPick = competitionPickByPlayerId[row.player_id];
                  const showNote = includeNotes && rowHasNote(row);
                  const traits = includeAttributes
                    ? traitFieldsForPlayer(row).filter(
                        ({ key }) => row[key] != null && row[key] !== ""
                      )
                    : [];

                  return (
                    <article
                      key={`${row.player_id}-${row.rank}`}
                      className={[
                        "independent-ranking-entry",
                        cardsOnlyLayout ? "independent-ranking-entry--card-only" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="independent-ranking-entry-card-wrap">
                        <PlayerCard
                          playerData={player}
                          ranking={row.rank}
                          miniView={Boolean(player?.dob)}
                          shortView={!player?.dob}
                        />
                        {competitionPick && (
                          <div className="independent-ranking-competition-pick">
                            Drafted <strong>#{competitionPick.overall_pick}</strong>
                            {" · "}
                            <strong>+{competitionPick.points}</strong> pts
                          </div>
                        )}
                      </div>
                      {!cardsOnlyLayout && showNote && (
                            <div className="independent-ranking-note">
                              <p>{row.note.trim()}</p>
                            </div>
                      )}
                      {!cardsOnlyLayout && traits.length > 0 && (
                            <div
                              className={[
                                "independent-ranking-traits",
                                isGoalie(row)
                                  ? "independent-ranking-traits--goalie"
                                  : "independent-ranking-traits--skater",
                              ].join(" ")}
                            >
                              {traits.map(({ key, label }) => (
                                <div
                                  key={key}
                                  className="independent-ranking-trait"
                                >
                                  <span className="independent-ranking-trait-label">
                                    {label}
                                  </span>
                                  <span className="independent-ranking-trait-value">
                                    {row[key]}
                                  </span>
                                </div>
                              ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>

              <ReviewDiscussionSection
                reviewType="independent"
                reviewId={independent_ranking_id}
                title="Ranking discussion"
              />
            </>
          )}
          {!loading && !ranking && (
            <Typography sx={{ color: "var(--text-muted)" }}>
              Ranking not found.
            </Typography>
          )}
        </Container>
      </div>
      {canShare && (
        <div
          className={[
            "independent-ranking-share-fab",
            showFloatingShare ? "is-visible" : "",
          ].join(" ")}
          aria-hidden={!showFloatingShare}
        >
          <Button
            type="button"
            variant="contained"
            size="medium"
            className="independent-ranking-share-fab-btn"
            startIcon={<ShareOutlinedIcon />}
            onClick={handleShare}
            tabIndex={showFloatingShare ? 0 : -1}
          >
            Share ranking
          </Button>
        </div>
      )}
      <Snackbar
        open={shareSnackbarOpen}
        autoHideDuration={4000}
        onClose={handleShareSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleShareSnackbarClose}
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
            backgroundColor: "var(--bg-elevated)",
            color: "var(--text-primary)",
            border: "1px solid var(--border-default)",
            "& .MuiAlert-icon": { color: "var(--brand-strong)" },
          }}
        >
          {shareSnackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default IndependentRanking;
